/**
 * Claim Your Card — full page for the /claim route.
 *
 * 3-step flow: enter card serial → confirm → digital twin minted in your
 * locker. All on-brand with the LEGENDS yellow→orange→red gradient.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, ScanLine, CheckCircle2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import HighlightedText from "../components/common/HighlightedText";
import ThirdPartyClaimForm from "../components/claims/ThirdPartyClaimForm";
import MyClaimsStatus from "../components/common/MyClaimsStatus";
import { useAuth } from "../App";

const StepCard = ({ number, icon: Icon, title, description }) => (
  <div className="relative bg-[#0a0a12] rounded-xl p-6 border border-white/[0.06] hover:border-orange-500/35 transition-all">
    {/* Step number in brand gradient corner */}
    <div className="absolute -top-3 -right-3 w-9 h-9 brand-gradient-bg rounded-full flex items-center justify-center font-black text-black text-base shadow-[0_0_15px_-3px_rgba(239,68,68,0.55)]">
      {number}
    </div>
    {/* Icon in brand-gradient frame */}
    <div className="w-12 h-12 brand-gradient-bg rounded-xl flex items-center justify-center mb-4 shadow-[0_0_22px_-5px_rgba(249,115,22,0.45)]">
      <Icon className="text-black" size={22} strokeWidth={2.5} />
    </div>
    <h3 className="text-white font-black text-lg mb-2">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">
      <HighlightedText text={description} />
    </p>
  </div>
);

const ClaimYourCardPage = () => {
  const navigate = useNavigate();
  const [serial, setSerial] = useState("");
  const [claimsRefresh, setClaimsRefresh] = useState(0);
  const [tpPrefillSerial, setTpPrefillSerial] = useState("");
  const { user, token } = useAuth();

  const handleSubmit = (e) => {
    e.preventDefault();
    const s = serial.trim().toUpperCase();
    if (!s) return;
    // Only certified BLK PACKS serials (COA-* / BLK*) live on the EMERGE
    // ledger. Anything else (e.g. the numeric sticker on a slab bought on
    // eBay/Mercari) is a THIRD-PARTY claim — route it to the claim form
    // below instead of dead-ending on the EMERGE verify page.
    if (/^(COA-|BLK)/i.test(s)) {
      navigate(`/verify?id=${encodeURIComponent(s)}`);
      return;
    }
    setTpPrefillSerial(serial.trim());
    toast.message("That looks like a slab sticker serial — finish your third-party claim below", {
      description: "Pick where you bought it and attach your receipt, then hit Submit.",
    });
    setTimeout(() => {
      document.getElementById("third-party-claim")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-black">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 brand-gradient-bg text-black px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.25em] mb-4">
            <Sparkles size={12} />
            Physical → Digital
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">
            Claim Your <span className="brand-gradient-text">Card</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-base leading-relaxed">
            <HighlightedText text="Got a Physical Card stamped with a Drop ID™? Activate its Digital Collectible companion and unlock its Certificate of Authenticity, AR experience, and Locker entry — all in three taps." />
          </p>
        </div>

        {/* 3-step flow */}
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          <StepCard
            number="1"
            icon={ScanLine}
            title="Scan or Enter Serial"
            description="Scan the QR Code on the back of your card OR type the Card ID / Serial Number printed beneath it."
          />
          <StepCard
            number="2"
            icon={ShieldCheck}
            title="Verify Authenticity"
            description="EMERGE checks the Verification ID against the public ledger to confirm it's a genuine BLK PACKS Mint."
          />
          <StepCard
            number="3"
            icon={Package}
            title="Mint to Locker"
            description="Your Digital Collectible drops into your Locker instantly — Trade, AR-preview, or stake it however you want."
          />
        </div>

        {/* Serial entry form */}
        <div className="journey-glow bg-[#0a0a12] rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 brand-gradient-bg rounded-xl flex items-center justify-center shadow-[0_0_20px_-5px_rgba(249,115,22,0.55)]">
              <CheckCircle2 className="text-black" size={20} strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-white font-black text-lg">Enter Your <span className="brand-gradient-text">Card</span> Details</h2>
              <p className="text-gray-500 text-sm">
                <HighlightedText text="Look on the back of your card for the Verification ID, Card ID, or Serial Number." />
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="COA-XX-2026-BLK000018  ·  BLK-KIDS-001  ·  BLK000042"
              className="flex-1 bg-black/60 border border-white/10 focus:border-orange-500/60 transition-colors rounded-lg px-4 py-3.5 text-white placeholder-white/30 text-sm outline-none font-mono"
              data-testid="claim-card-input"
              autoFocus
            />
            <button
              type="submit"
              className="brand-gradient-btn font-black px-6 py-3.5 rounded-lg text-sm uppercase tracking-wider"
              data-testid="claim-card-submit-btn"
            >
              <ScanLine size={16} strokeWidth={2.5} />
              Claim Now
              <ArrowRight size={14} strokeWidth={2.5} />
            </button>
          </form>

          <div className="mt-5 pt-5 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
            <span data-testid="claim-third-party-hint">
              Bought on eBay / Mercari / another marketplace?{" "}
              <button
                onClick={() => document.getElementById("third-party-claim")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="brand-gradient-text font-bold hover:brightness-110 transition"
                data-testid="claim-scroll-to-tp-btn"
              >
                Submit a Third-Party Claim
              </button>
            </span>
            <span>
              No Card yet? <button onClick={() => navigate("/drops")} className="brand-gradient-text font-bold hover:brightness-110 transition">Browse Drops</button>
            </span>
            <span>
              Need help? <button onClick={() => navigate("/contact")} className="brand-gradient-text font-bold hover:brightness-110 transition">Contact Support</button>
            </span>
          </div>
        </div>

        {/* Third-party marketplace claim (eBay / Amazon / slab purchases) */}
        <ThirdPartyClaimForm
          user={user}
          token={token}
          initialSerial={tpPrefillSerial}
          onClaimSubmitted={() => setClaimsRefresh((v) => v + 1)}
        />

        {/* My Claims status tracker */}
        <div className="max-w-2xl mx-auto mt-8">
          <MyClaimsStatus token={token} refreshKey={claimsRefresh} />
        </div>
      </div>
    </div>
  );
};

export default ClaimYourCardPage;
